import { useCallback, useEffect, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { Link } from "react-router-dom";
import { apiErrorText, readApiErrorResponse } from "@/lib/api-error";
import { apiFetch } from "@/services/http/api-fetch";
import { CourseAnnouncementsPanel } from "@/components/instructor/course-announcements-panel";
import { useAuthStore } from "@/stores/authStore";

type NotificationRow = {
  id: string;
  type: string;
  payload: {
    title?: string;
    body?: string;
    senderId?: string;
    senderName?: string | null;
    /** COMMS-14: "ai_escalation" — a student's "Ask the instructor" message. */
    kind?: string;
    zoomUrl?: string;
    courseId?: string;
    courseSlug?: string;
    announcementId?: string;
    cohortEventId?: string;
    startsAt?: string;
    cancelled?: boolean;
  };
  readAt: string | null;
  createdAt: string;
};

type NotificationsPage = {
  notifications: NotificationRow[];
  nextCursor?: string | null;
  unreadCount: number;
};

/** COMM-17: how often the inbox refreshes while the tab is visible. */
export const NOTIFICATION_POLL_MS = 60_000;

async function fetchNotifications(cursor: string | null, signal?: AbortSignal) {
  const path = cursor
    ? `/v1/notifications?cursor=${encodeURIComponent(cursor)}`
    : "/v1/notifications";
  const res = await apiFetch(path, { signal });
  if (!res.ok) throw await readApiErrorResponse(res);
  return ((await res.json()) as { data: NotificationsPage }).data;
}

type StaffCourse = {
  id: string;
  slug: string;
  title: string;
};

type NotificationCentreProps = {
  isStaff?: boolean;
};

export function NotificationCentre({ isStaff = false }: NotificationCentreProps) {
  const t = useTranslations("notifications");
  const tRoot = useTranslations();
  const locale = useLocale();
  const tenantSlug = useAuthStore((state) => state.user?.tenantSlug ?? "");
  const [rows, setRows] = useState<NotificationRow[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  /** Once the reader pages back, the refresh must not move their "Load more" position. */
  const pagedRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [courses, setCourses] = useState<StaffCourse[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [loadingCourses, setLoadingCourses] = useState(isStaff);

  const errorText = useCallback(
    (err: unknown, fallbackKey: string) =>
      apiErrorText(err, tRoot, fallbackKey, { namespace: "notifications.errors" }),
    [tRoot],
  );

  /** COMM-10: dates follow the app's language, not the browser's. */
  const formatWhen = useCallback(
    (iso: string) =>
      new Date(iso).toLocaleString(locale === "hi" ? "hi-IN" : "en-IN", {
        dateStyle: "medium",
        timeStyle: "short",
      }),
    [locale],
  );

  /**
   * The first page, fetched on open and every minute while the tab is visible (COMM-17). Rows the
   * reader already loaded with "Load more" are kept below the refreshed first page.
   */
  const refresh = useCallback(
    async (signal?: AbortSignal) => {
      try {
        const data = await fetchNotifications(null, signal);
        setRows((current) => {
          const fresh = new Set(data.notifications.map((row) => row.id));
          const older = current.filter((row) => !fresh.has(row.id));
          return [...data.notifications, ...older];
        });
        setUnreadCount(data.unreadCount);
        if (!pagedRef.current) setNextCursor(data.nextCursor ?? null);
        setError(null);
      } catch (err) {
        if ((err as Error)?.name === "AbortError") return;
        setError(errorText(err, "notifications.loadFailed"));
      } finally {
        setLoading(false);
      }
    },
    [errorText],
  );

  useEffect(() => {
    // Leaving the page truncates the response; the abort stops it being read.
    const controller = new AbortController();
    void refresh(controller.signal);
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void refresh(controller.signal);
    }, NOTIFICATION_POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") void refresh(controller.signal);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      controller.abort();
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refresh]);

  async function loadMore() {
    if (!nextCursor || loadingMore) return;
    pagedRef.current = true;
    setLoadingMore(true);
    try {
      const data = await fetchNotifications(nextCursor);
      setRows((current) => {
        const known = new Set(current.map((row) => row.id));
        return [...current, ...data.notifications.filter((row) => !known.has(row.id))];
      });
      setNextCursor(data.nextCursor ?? null);
      setUnreadCount(data.unreadCount);
    } catch (err) {
      setError(errorText(err, "notifications.loadFailed"));
    } finally {
      setLoadingMore(false);
    }
  }

  useEffect(() => {
    if (!isStaff) return;

    const controller = new AbortController();
    void (async () => {
      try {
        const res = await apiFetch("/v1/instructor/courses", {
          signal: controller.signal,
        });
        const json = await res.json();
        if (res.ok) {
          const nextCourses = json.data.courses as StaffCourse[];
          setCourses(nextCourses);
          if (nextCourses.length > 0) {
            setSelectedCourseId(nextCourses[0]!.id);
          }
        }
        setLoadingCourses(false);
      } catch (err) {
        if ((err as Error)?.name === "AbortError") return;
        setLoadingCourses(false);
      }
    })();
    return () => controller.abort();
  }, [isStaff]);

  async function markRead(notificationId: string) {
    setError(null);
    try {
      const res = await apiFetch(`/v1/notifications/${notificationId}/read`, { method: "POST" });
      if (!res.ok) throw await readApiErrorResponse(res);
      setRows((current) =>
        current.map((row) =>
          row.id === notificationId ? { ...row, readAt: new Date().toISOString() } : row,
        ),
      );
      setUnreadCount((count) => Math.max(0, count - 1));
    } catch (err) {
      setError(errorText(err, "notifications.markReadFailed"));
    }
  }

  async function markAllRead() {
    setError(null);
    try {
      const res = await apiFetch("/v1/notifications/read-all", { method: "POST" });
      if (!res.ok) throw await readApiErrorResponse(res);
      const now = new Date().toISOString();
      setRows((current) => current.map((row) => (row.readAt ? row : { ...row, readAt: now })));
      setUnreadCount(0);
    } catch (err) {
      setError(errorText(err, "notifications.markReadFailed"));
    }
  }

  function resolveTitle(row: NotificationRow): string {
    if (row.type === "direct_message" && row.payload.kind === "ai_escalation") {
      return row.payload.senderName
        ? t("aiEscalationFrom", { name: row.payload.senderName })
        : t("aiEscalationTitle");
    }
    if (row.type === "direct_message") {
      return row.payload.senderName
        ? t("directMessageFrom", { name: row.payload.senderName })
        : t("directMessageTitle");
    }
    if (row.type === "cohort_live_zoom") {
      return row.payload.title ?? t("cohortLiveTitle");
    }
    return row.payload.title ?? t("defaultTitle");
  }

  function resolveBody(row: NotificationRow): string | null {
    // COMMS-14: the stored DM text is English; the escalation is described in the viewer's language.
    if (row.type === "direct_message" && row.payload.kind === "ai_escalation") {
      return t("aiEscalationBody");
    }
    if (row.type === "cohort_live_zoom" && row.payload.startsAt) {
      const when = formatWhen(row.payload.startsAt);
      if (row.payload.cancelled) {
        return t("cohortLiveCancelledAt", { when });
      }
      return t("cohortLiveScheduledAt", { when });
    }
    return row.payload.body ?? null;
  }

  if (loading) {
    return <p className="text-sm text-[var(--text-muted)]">{t("loading")}</p>;
  }

  return (
    <div className="space-y-8">
      {isStaff ? (
        <section
          className="rounded-xl border border-[var(--brand-purple)]/15 bg-[var(--brand-surface)] p-5"
          data-testid="notifications-announce-panel"
        >
          <h2 className="text-lg font-semibold">{t("announceTitle")}</h2>
          <p className="mt-1 text-sm text-[var(--text-muted)]">{t("announceHint")}</p>
          {loadingCourses ? (
            <p className="mt-4 text-sm text-[var(--text-muted)]">{t("loadingCourses")}</p>
          ) : courses.length === 0 ? (
            <p className="mt-4 text-sm text-[var(--text-muted)]">{t("noCourses")}</p>
          ) : (
            <>
              <label className="mt-4 block text-sm">
                <span className="mb-1 block font-medium">{t("courseLabel")}</span>
                <select
                  data-testid="notifications-announce-course"
                  className="w-full max-w-md rounded-md border border-[var(--border-strong)] px-3 py-2"
                  value={selectedCourseId}
                  onChange={(e) => setSelectedCourseId(e.target.value)}
                >
                  {courses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.title}
                    </option>
                  ))}
                </select>
              </label>
              {selectedCourseId ? (
                <div className="mt-6">
                  <CourseAnnouncementsPanel courseId={selectedCourseId} />
                </div>
              ) : null}
            </>
          )}
        </section>
      ) : null}

      <section data-testid="notification-centre">
        <h2 className="text-lg font-semibold">{t("inboxTitle")}</h2>
        <div className="mt-1 flex flex-wrap items-center gap-3">
          <p className="text-sm text-[var(--text-muted)]">
            {t("unreadCount", { count: unreadCount })}
          </p>
          {unreadCount > 0 ? (
            <button
              type="button"
              onClick={() => void markAllRead()}
              className="text-xs text-[var(--text-heading)] underline"
              data-testid="notifications-mark-all-read"
            >
              {t("markAllRead")}
            </button>
          ) : null}
        </div>
        {error ? (
          <p
            role="alert"
            className="mt-2 text-sm text-[var(--danger)]"
            data-testid="notifications-error"
          >
            {error}
          </p>
        ) : null}
        {rows.length === 0 ? (
          <p className="mt-4 text-sm text-[var(--text-muted)]">{t("empty")}</p>
        ) : (
          <ul className="mt-4 space-y-3">
            {rows.map((row) => {
              const body = resolveBody(row);
              return (
                <li
                  key={row.id}
                  className={`rounded-lg border p-4 text-sm ${row.readAt ? "border-[var(--border)]" : " border-[var(--accent-border)] bg-[var(--accent-soft)]/30"}`}
                  data-testid={`notification-${row.id}`}
                >
                  <p className="font-medium">{resolveTitle(row)}</p>
                  <time
                    dateTime={row.createdAt}
                    className="block text-xs text-[var(--text-muted)]"
                    data-testid={`notification-time-${row.id}`}
                  >
                    {formatWhen(row.createdAt)}
                  </time>
                  {body ? <p className="mt-1 text-[var(--text-secondary)]">{body}</p> : null}
                  {row.type === "cohort_live_zoom" && row.payload.zoomUrl ? (
                    <a
                      href={row.payload.zoomUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-2 inline-block text-sm text-[var(--text-heading)] underline"
                      data-testid={`notification-zoom-join-${row.id}`}
                    >
                      {t("cohortLiveJoin")}
                    </a>
                  ) : null}
                  {row.type === "direct_message" && row.payload.senderId && tenantSlug ? (
                    <Link
                      to={`/t/${tenantSlug}/messages?with=${encodeURIComponent(row.payload.senderId)}`}
                      className="mt-2 mr-4 inline-block text-sm text-[var(--text-heading)] underline"
                      data-testid={`notification-open-conversation-${row.id}`}
                    >
                      {t("openConversation")}
                    </Link>
                  ) : null}
                  {row.type === "course_announcement" && row.payload.courseSlug && tenantSlug ? (
                    <Link
                      to={`/t/${tenantSlug}/learn/${encodeURIComponent(row.payload.courseSlug)}/announcements${row.payload.announcementId ? `#a-${row.payload.announcementId}` : ""}`}
                      className="mt-2 mr-4 inline-block text-sm text-[var(--text-heading)] underline"
                      data-testid={`notification-open-announcement-${row.id}`}
                    >
                      {t("openAnnouncements")}
                    </Link>
                  ) : null}
                  {!row.readAt ? (
                    <button
                      type="button"
                      onClick={() => void markRead(row.id)}
                      className="mt-2 text-xs text-[var(--text-heading)] underline"
                      data-testid={`notification-mark-read-${row.id}`}
                    >
                      {t("markRead")}
                    </button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
        {nextCursor ? (
          <button
            type="button"
            onClick={() => void loadMore()}
            disabled={loadingMore}
            className="mt-4 rounded border border-[var(--border-strong)] px-3 py-1.5 text-sm disabled:opacity-50"
            data-testid="notifications-load-more"
          >
            {loadingMore ? t("loading") : t("loadMore")}
          </button>
        ) : null}
      </section>
    </div>
  );
}
